// src/lib/axios.js
import axios from "axios";
import { API_BASE, getCsrfToken } from "./api";

// Build a dedicated instance
export const http = axios.create({
  baseURL: API_BASE,          // relative URLs resolve against this
  withCredentials: true,
  timeout: 20000,
});

/**
 * OPTIONAL: only use this if you truly need to rewrite a known, old host.
 * Otherwise, just return the url unchanged and rely on baseURL handling.
 *
 * Example: rewrite from "http://127.0.0.1:8000" to API_BASE.
 */
function rewriteUrl(url) {
  if (!url) return url;

  // 1) Never rewrite absolute URLs
  if (/^https?:\/\//i.test(url)) return url;

  // 2) For relative URLs, do nothing — let axios join with baseURL.
  return url;

  // If you MUST replace a specific legacy prefix, do it like this:
  // const LEGACY_PREFIX = "/api"; // example only
  // if (url.startsWith(LEGACY_PREFIX)) return url; // keep relative
  // return url;
}

// Attach CSRF for unsafe methods (except token endpoint)
http.interceptors.request.use((config) => {
  config = { ...config }; // avoid mutating original

  const method = (config.method || "get").toLowerCase();
  const unsafe = method !== "get" && method !== "head";

  // Normalize URL (safe version)
  if (config.url) config.url = rewriteUrl(config.url);

  // Special case: OAuth token exchange does NOT use cookies/CSRF
  const isToken = (config.url || "").includes("/o/token");
  if (isToken) {
    config.withCredentials = false; // DOT token endpoint uses client auth, not session
    // Don't auto-attach Bearer either:
    config.skipAuth = true;
    return config;
  }

  // Attach CSRF for unsafe methods (if you’re using session auth anywhere)
  if (unsafe) {
    config.headers = config.headers || {};
    if (!config.headers["X-CSRFToken"]) {
      const csrf = getCsrfToken();
      if (csrf) config.headers["X-CSRFToken"] = csrf;
    }
  }

  // Attach Authorization unless explicitly skipped
  if (!config.skipAuth) {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Convenience: if caller marks a request as "public", set both flags
  if (config.public === true) {
    config.skipAuth = true;
    config.ignore401 = true;
  }

  return config;
});

// Helper to consistently detect "our API" even when url is absolute
function isOurApiUrl(url) {
  try {
    const u = new URL(url, API_BASE); // resolves relative against API_BASE
    const p = u.pathname || "";
    return (
      p.startsWith("/api") ||
      p.startsWith("/artists") ||
      p.startsWith("/hosts")
    );
  } catch {
    return false;
  }
}

// Redirect on 401 ONLY if we expected auth or user is logged in
http.interceptors.response.use(
  (res) => res,
  (error) => {
    const resp = error?.response;
    const cfg  = error?.config || {};
    const status = resp?.status;
    const url = cfg?.url || "";

    const isApi = isOurApiUrl(url);
    const authed = !!localStorage.getItem("access_token");
    const requireAuth = cfg.requireAuth === true; // explicitly private
    const ignore401 = cfg.ignore401 === true;     // explicitly ignore
    const isAuthEndpoint =
      url.startsWith("/o/token/") ||
      url.startsWith("/api/token/") ||
      url.startsWith("/api/token/refresh/");

    if (status === 401 && isApi && !ignore401 && !isAuthEndpoint) {
      // Only force redirect if this call required auth OR the user was authenticated
      if (requireAuth || authed) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("access_expires_at");
        localStorage.removeItem("user_role");
        setTimeout(() => (window.location.href = "/login"), 0);
      }
      // Otherwise, let the page handle it (don't redirect guests).
    }

    return Promise.reject(error);
  }
);
